import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/client";
import { useCustomer } from "../context/CustomerContext";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import { formatDateTime } from "../utils/datetime";

/**
 * Multi-provider cloud object-storage browser, scoped to the customer in the
 * global switcher. Chains connector → (Azure account) → container/bucket →
 * objects, with download (signed URL), upload, and delete. Backed by the
 * generic /api/v1/cloud-connectors/{id}/storage/... endpoints that dispatch by
 * the connector's platform (Azure Blob, AWS S3, GCP Cloud Storage). List is
 * read-only; upload/delete are admin-only + audited server-side.
 */

interface Connector {
  id: number;
  platform: string;
  name: string;
  enabled: boolean;
  customer_id: number;
}
interface NamedItem {
  name: string;
}
interface StorageObject {
  name: string;
  size: number;
  last_modified: string | null;
  content_type: string | null;
}

const STORAGE_PLATFORMS = ["azure", "aws", "gcp"];
const LABELS: Record<
  string,
  { provider: string; container: string; object: string; needsAccount: boolean }
> = {
  azure: { provider: "Azure Blob", container: "Container", object: "Blob", needsAccount: true },
  aws: { provider: "AWS S3", container: "Bucket", object: "Object", needsAccount: false },
  gcp: { provider: "Google Cloud Storage", container: "Bucket", object: "Object", needsAccount: false },
};

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export default function CloudStorage() {
  const { currentCustomerId, current } = useCustomer();
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [connId, setConnId] = useState<number | "">("");
  const [accounts, setAccounts] = useState<NamedItem[]>([]);
  const [account, setAccount] = useState("");
  const [containers, setContainers] = useState<NamedItem[]>([]);
  const [container, setContainer] = useState("");
  const [objects, setObjects] = useState<StorageObject[]>([]);
  const [prefix, setPrefix] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const objSort = useSort("name", "asc");

  const conn = connectors.find((c) => c.id === connId);
  const platform = conn?.platform || "";
  const L = LABELS[platform];
  const isAzure = platform === "azure";

  const errMsg = (e: unknown, fallback: string) =>
    (e as { response?: { data?: { detail?: string } } }).response?.data?.detail || fallback;

  // Connectors scoped to the selected customer (and to storage platforms).
  useEffect(() => {
    const params: Record<string, unknown> = {};
    if (currentCustomerId != null) params.customer_id = currentCustomerId;
    api
      .get("/api/v1/cloud-connectors", { params })
      .then((r) =>
        setConnectors(
          r.data.filter((c: Connector) => STORAGE_PLATFORMS.includes(c.platform) && c.enabled),
        ),
      )
      .catch(() => setConnectors([]));
    setConnId("");
    setAccount("");
    setAccounts([]);
    setContainer("");
    setContainers([]);
    setObjects([]);
  }, [currentCustomerId]);

  // Connector chosen: Azure → list its storage accounts; AWS/GCP → buckets load
  // directly (no account layer) via the container effect below.
  useEffect(() => {
    setAccounts([]);
    setAccount("");
    setContainers([]);
    setContainer("");
    setObjects([]);
    if (connId === "" || !conn) return;
    if (conn.platform === "azure") {
      setBusy(true);
      setError("");
      api
        .get(`/api/v1/cloud-connectors/${connId}/storage-accounts`)
        .then((r) => setAccounts(r.data))
        .catch((e: unknown) => setError(errMsg(e, "Couldn't list storage accounts")))
        .finally(() => setBusy(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connId]);

  const loadContainers = useCallback(() => {
    setContainers([]);
    setContainer("");
    setObjects([]);
    if (connId === "" || !conn) return;
    if (conn.platform === "azure" && !account) return;
    setBusy(true);
    setError("");
    const params: Record<string, unknown> = {};
    if (conn.platform === "azure") params.account = account;
    api
      .get(`/api/v1/cloud-connectors/${connId}/storage/containers`, { params })
      .then((r) => setContainers(r.data))
      .catch((e: unknown) =>
        setError(errMsg(e, "Couldn't list containers/buckets — check the connector's data-plane permissions.")),
      )
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connId, account]);
  useEffect(() => {
    loadContainers();
  }, [loadContainers]);

  const loadObjects = useCallback(() => {
    if (connId === "" || !conn || !container || (conn.platform === "azure" && !account)) {
      setObjects([]);
      return;
    }
    setBusy(true);
    setError("");
    const params: Record<string, unknown> = { container, prefix };
    if (conn.platform === "azure") params.account = account;
    api
      .get(`/api/v1/cloud-connectors/${connId}/storage/objects`, { params })
      .then((r) => setObjects(r.data))
      .catch((e: unknown) => setError(errMsg(e, "Couldn't list objects")))
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connId, account, container, prefix]);
  useEffect(() => {
    loadObjects();
  }, [loadObjects]);

  const storageParams = (extra: Record<string, unknown>) => ({
    ...(isAzure ? { account } : {}),
    container,
    ...extra,
  });

  const download = async (name: string) => {
    setError("");
    try {
      const r = await api.get(`/api/v1/cloud-connectors/${connId}/storage/download-url`, {
        params: storageParams({ name }),
      });
      window.open(r.data.url, "_blank", "noopener");
    } catch (e: unknown) {
      setError(errMsg(e, "Couldn't generate download link"));
    }
  };

  const del = async (name: string) => {
    if (!window.confirm(`Permanently delete "${name}"? This cannot be undone.`)) return;
    setError("");
    try {
      await api.delete(`/api/v1/cloud-connectors/${connId}/storage/object`, {
        params: storageParams({ name }),
      });
      loadObjects();
    } catch (e: unknown) {
      setError(errMsg(e, "Delete failed"));
    }
  };

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f || connId === "" || !conn || !container || (isAzure && !account)) return;
    setBusy(true);
    setError("");
    const fd = new FormData();
    fd.append("container", container);
    fd.append("name", (prefix || "") + f.name);
    if (isAzure) fd.append("account", account);
    fd.append("file", f);
    try {
      await api.post(`/api/v1/cloud-connectors/${connId}/storage/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (fileRef.current) fileRef.current.value = "";
      loadObjects();
    } catch (e: unknown) {
      setError(errMsg(e, "Upload failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Cloud Storage</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Browse object storage (Azure Blob, AWS S3, Google Cloud Storage) for{" "}
          <span className="font-medium text-gray-700 dark:text-gray-300">{current ? current.name : "all customers"}</span>
          . Change the customer with the header switcher.
        </p>
      </div>

      {connectors.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">
          No enabled storage connectors{current ? ` for ${current.name}` : ""}. Add a cloud
          connector (Azure / AWS / GCP) on the customer's page — Customers →{" "}
          {current ? current.name : "the customer"} → Cloud tab — then enable it and it'll show
          up here for browsing.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <select
              value={connId}
              onChange={(e) => setConnId(e.target.value ? Number(e.target.value) : "")}
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-900 dark:text-gray-100"
            >
              <option value="">Storage connector…</option>
              {connectors.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} — {LABELS[c.platform]?.provider || c.platform}
                </option>
              ))}
            </select>

            {isAzure && (
              <select
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                disabled={accounts.length === 0}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-900 dark:text-gray-100 disabled:opacity-50"
              >
                <option value="">Storage account…</option>
                {accounts.map((a) => (
                  <option key={a.name} value={a.name}>
                    {a.name}
                  </option>
                ))}
              </select>
            )}

            <select
              value={container}
              onChange={(e) => setContainer(e.target.value)}
              disabled={containers.length === 0}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white disabled:opacity-50"
            >
              <option value="">{L ? `${L.container}…` : "Container…"}</option>
              {containers.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>

            {container && (
              <input
                type="text"
                placeholder="prefix filter (e.g. folder/)"
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm w-56 dark:bg-gray-900 dark:text-gray-100"
              />
            )}
          </div>

          {error && (
            <p className="text-xs text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg mb-3">
              {error}
            </p>
          )}

          {container && (
            <div className="flex items-center gap-2 mb-3">
              <input ref={fileRef} type="file" className="text-xs" />
              <button
                onClick={upload}
                disabled={busy}
                className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Upload
              </button>
              <span className="text-xs text-gray-400 dark:text-gray-500">
                Uploads land under the current prefix. Max 100 MB.
              </span>
            </div>
          )}

          {container ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 uppercase">
                  <tr>
                    <SortableTh label="Name" sortKey="name" sort={objSort.sort} onSort={objSort.toggle} />
                    <SortableTh label="Size" sortKey="size" sort={objSort.sort} onSort={objSort.toggle} align="right" />
                    <SortableTh label="Modified" sortKey="last_modified" sort={objSort.sort} onSort={objSort.toggle} />
                    <th className="px-4 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {sortRows(objects, objSort.sort, (b, key) => {
                    switch (key) {
                      case "size": return b.size;
                      case "last_modified": return b.last_modified ? Date.parse(b.last_modified) : 0;
                      default: return (b.name || "").toLowerCase();
                    }
                  }).map((b) => (
                    <tr key={b.name} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                      <td className="px-4 py-2 font-mono text-xs">{b.name}</td>
                      <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-400">{fmtBytes(b.size)}</td>
                      <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
                        {b.last_modified ? formatDateTime(b.last_modified) : "—"}
                      </td>
                      <td className="px-4 py-2 space-x-3">
                        <button
                          onClick={() => download(b.name)}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          Download
                        </button>
                        <button
                          onClick={() => del(b.name)}
                          className="text-xs text-red-600 hover:underline"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {objects.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-10 text-center text-gray-400 dark:text-gray-500 text-sm">
                        {busy ? "Loading…" : `No ${L ? L.object.toLowerCase() : "object"}s here.`}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500 italic">
              Pick a connector{isAzure ? ", storage account," : ""} and {L ? L.container.toLowerCase() : "container"} to browse.
            </p>
          )}
        </>
      )}
    </div>
  );
}
